import React from "react";
import { FiDownload, FiInfo } from "react-icons/fi";
import {
  HiOutlineDocumentText,
} from "react-icons/hi2";
import {
  BiRefresh,
  BiErrorCircle,
} from "react-icons/bi";

import { useIncident } from '../context/incidentContext';
import { evidenceUrl, formatDate, incidentId } from '../incidentData';

export default function IncidentDashboardBottom() {
  const { actions, activity, documents: allDocuments, setPanel, emptyMessage } = useIncident();
  const outstandingActions = actions.slice(0, 4).map(row => ({ id: row.id, action: row.correctiveAction,
    relatedTo: incidentId(row.id), assignedTo: row.referredTo?.fullName || 'Unassigned', dueDate: 'Not tracked', daysOverdue: '—' }));
  const recentActivity = activity.slice(0, 4).map(row => ({ ...row, date: formatDate(row.date),
    icon: row.type === 'update' ? BiRefresh : BiErrorCircle,
    iconColor: row.type === 'update' ? 'text-amber-500 bg-amber-50 border-amber-100' : 'text-rose-500 bg-rose-50 border-rose-100',
  }));
  const documents = allDocuments.slice(0, 4);

  return (
    <div className="w-full bg-slate-50/50 p-4 font-sans text-slate-800">
      {/* 3-Column Grid Panel View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">

        {/* =====================================================
            PANEL 1: OUTSTANDING ACTIONS
        ====================================================== */}
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col justify-between overflow-hidden">
          <div>
            {/* Header */}
            <div className="px-4 py-3.5 flex items-center justify-between border-b border-slate-100">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-bold text-slate-900 tracking-tight">
                  Outstanding Actions
                </h3>

                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-rose-50 text-rose-500 rounded border border-rose-100/60">
                  {actions.length}
                </span>
              </div>

              <button type="button" onClick={() => setPanel({ kind: "actions" })} className="text-[11px] font-semibold text-blue-600 hover:underline">
                View all
              </button>
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[340px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/30 text-[10px] font-bold text-slate-400 tracking-wide">
                    <th className="py-2 px-4">Action</th>
                    <th className="py-2 px-2">Related To</th>
                    <th className="py-2 px-2">Assigned To</th>
                    <th className="py-2 px-2">Due Date</th>
                    <th className="py-2 px-4 text-center">
                      Days Overdue
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100 text-[11px]">
                  {!outstandingActions.length && <tr><td colSpan={5} className="py-5 px-4 text-xs text-slate-400">{emptyMessage('No saved outstanding corrective actions.')}</td></tr>}
                  {outstandingActions.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-slate-50/40 transition-colors"
                    >
                      <td className="py-2.5 px-4 font-bold text-slate-700 max-w-[120px] truncate">
                        <button type="button" onClick={() => setPanel({ kind: "details", id: row.id })} className="text-left font-bold">{row.action}</button>
                      </td>

                      <td className="py-2.5 px-2 font-bold text-blue-600 whitespace-nowrap">
                        {row.relatedTo}
                      </td>

                      <td className="py-2.5 px-2 text-slate-500 font-medium whitespace-nowrap">
                        {row.assignedTo}
                      </td>

                      <td className="py-2.5 px-2 text-slate-500 font-medium whitespace-nowrap">
                        {row.dueDate}
                      </td>

                      <td className="py-2.5 px-4 text-center font-bold text-rose-600">
                        {row.daysOverdue}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-100 bg-slate-50/10">
            <button type="button" onClick={() => setPanel({ kind: "actions" })} className="text-xs font-bold text-blue-600 hover:underline">
              View all actions
            </button>
          </div>
        </div>

        {/* =====================================================
            PANEL 2: RECENT ACTIVITY
        ====================================================== */}
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col justify-between overflow-hidden">
          <div>
            {/* Header */}
            <div className="px-4 py-3.5 flex items-center justify-between border-b border-slate-100">
              <h3 className="text-xs font-bold text-slate-900 tracking-tight">
                Recent Activity
              </h3>

              <button type="button" onClick={() => setPanel({ kind: 'activity' })} className="text-[11px] font-semibold text-blue-600 hover:underline">
                View all
              </button>
            </div>

            {/* Timeline */}
            <div className="p-4 relative flex flex-col gap-4">
              {/* Central Timeline Connector */}
              <div className="absolute left-[30px] top-6 bottom-6 w-0.5 bg-slate-100 pointer-events-none" />

              {!recentActivity.length && <p className="text-xs text-slate-400">{emptyMessage('No saved incident activity in this range.')}</p>}
              {recentActivity.map((activity) => {
                const IconComponent = activity.icon;

                return (
                  <div
                    key={activity.id}
                    role="button" tabIndex={0}
                    onClick={() => setPanel({ kind: "details", id: activity.incidentId })}
                    onKeyDown={event => { if (event.key === "Enter") setPanel({ kind: "details", id: activity.incidentId }); }}
                    className="relative flex items-start gap-3 text-[11px] min-w-0 z-10"
                  >
                    {/* Activity Icon */}
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 border ${activity.iconColor}`}
                    >
                      <IconComponent className="w-4 h-4" />
                    </div>

                    {/* Activity Content */}
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 min-w-0">
                      <div className="flex flex-col min-w-0">
                        <span className="font-bold text-slate-800 tracking-tight leading-snug">
                          {activity.title}
                        </span>

                        <span className="text-[10px] text-slate-400 font-medium mt-0.5 truncate">
                          {activity.subtitle}
                        </span>
                      </div>

                      <span className="text-[10px] text-slate-400 font-medium shrink-0 text-right whitespace-nowrap">
                        {activity.date}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Invisible Footer for Equal Height */}
          <div className="p-3 border-t border-slate-100 bg-slate-50/10 invisible">
            <span />
          </div>
        </div>

        {/* =====================================================
            PANEL 3: KEY DOCUMENTS & EVIDENCE
        ====================================================== */}
        <div className="bg-white border border-slate-200/80 rounded-xl shadow-sm flex flex-col justify-between overflow-hidden">
          <div>
            {/* Header */}
            <div className="px-4 py-3.5 flex items-center justify-between border-b border-slate-100">
              <h3 className="text-xs font-bold text-slate-900 tracking-tight">
                Key Documents & Evidence
              </h3>

              <button type="button" onClick={() => setPanel({ kind: 'documents' })} className="text-[11px] font-semibold text-blue-600 hover:underline">
                Manage
              </button>
            </div>

            {/* Documents */}
            <div className="divide-y divide-slate-100 px-4">
              {!documents.length && <p className="py-5 text-xs text-slate-400">{emptyMessage('No incident evidence saved in this range.')}</p>}
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="py-2.5 flex items-center justify-between gap-3 text-[11px]"
                >
                  <div className="flex items-start gap-2.5 min-w-0">
                    <HiOutlineDocumentText className="w-4 h-4 text-indigo-600 shrink-0 mt-0.5" />

                    <div className="flex flex-col min-w-0">
                      <span className="font-bold text-slate-700 tracking-tight truncate">
                        {doc.name}
                      </span>

                      <span className="text-[10px] text-slate-400 font-medium mt-0.5">
                        {doc.meta}
                      </span>
                    </div>
                  </div>

                  <a href={evidenceUrl(doc.url) || undefined} target="_blank" rel="noreferrer" aria-label={"Open " + doc.name} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-lg shrink-0 transition">
                    <FiDownload className="w-3.5 h-3.5" />
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-slate-100 bg-slate-50/10">
            <button type="button" onClick={() => setPanel({ kind: 'documents' })} className="text-xs font-bold text-blue-600 hover:underline">
              View all documents
            </button>
          </div>
        </div>
      </div>

      {/* =====================================================
          COMPLIANCE / HELP INFORMATION FOOTER
      ====================================================== */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-t border-slate-200/60 pt-4 px-1 text-xs">

        {/* Information */}
        <div className="flex items-start sm:items-center gap-2 text-slate-500 font-medium max-w-2xl leading-relaxed">
          <FiInfo className="w-4 h-4 text-slate-400 shrink-0 mt-0.5 sm:mt-0" />

          <span>
            Report all incidents and near misses as soon as possible.
            Timely reporting helps prevent recurrence and ensures compliance.
          </span>
        </div>

        {/* Policy Links */}
        <div className="flex items-center gap-4 font-bold text-blue-600 shrink-0">

          <button type="button" onClick={() => setPanel({ kind: "policy" })} className="inline-flex items-center gap-1.5 hover:underline">
            <HiOutlineDocumentText className="w-4 h-4 text-blue-600" />
            <span>Incident Reporting Policy</span>
          </button>

          <button type="button" onClick={() => setPanel({ kind: "riddor" })} className="inline-flex items-center gap-1.5 hover:underline">
            <HiOutlineDocumentText className="w-4 h-4 text-blue-600" />
            <span>RIDDOR Guidance</span>
          </button>

        </div>
      </div>
    </div>
  );
}
